import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuxiliarService } from './services/auxiliar.service';
import { filter, first, Observable, Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import _ from 'lodash';
import moment from 'moment';
import 'moment/locale/es';
import { PrimeNG } from 'primeng/config';
import { NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from './services/auth.service';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public spinner: Observable<boolean>;

  constructor(
    private translate: TranslateService,
    private auxiliarService: AuxiliarService,
    private messageService: MessageService,
    private primeConfig: PrimeNG,
    private router: Router,
    private cookieService: CookieService,
    private authService: AuthService,
    private logger: NGXLogger,
  ){
    // get spinner flag
    this.spinner = this.auxiliarService.spinner;
    // set navigator lang
    this.setNavigatorLang();
    // set ng-prime translations
    this.translate.get('primeng').subscribe(res => this.primeConfig.setTranslation(res));
    // observable to trigger toast
    this.auxiliarService.toastObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (_.get(value, 'message')) {
          this.messageService.add({
            severity: _.get(value, 'type', 'info'),
            summary: _.get(value, 'message'),
            key: 'toast',
            life: 5000,
          });
        }
    });
  }

  ngOnInit(): void {
    // get navigator tenant && set in cookies && set main colors
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        first()
      )
      .subscribe(() => {
        const urlTenant = this.router.url.split('/')[1] || 'benefit';
        // save in cookies
        this.cookieService.set('urlTenant', urlTenant, { path: '/' });
        // get features
        this.getUrlTenantFeatures(urlTenant);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private getUrlTenantFeatures(urlTenant: string): void {
    this.authService.getAnonymousFeatures(urlTenant)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.logger.debug('[authService] getAnonymousFeatures res: ', res);
          // id for login
          const id = _.get(res, 'body.data.id');
          if(id) this.cookieService.set('anonymous-id', id);
          // footer name
          const footerName = _.get(res, 'body.data.footerName');
          if(footerName) this.cookieService.set('footerName', footerName);
          // colors
          const colors = {
            c: _.get(res, 'body.data.colorComplementary'),
            m: _.get(res, 'body.data.colorMain'),
            mD: _.get(res, 'body.data.colorMainDarker'),
            mL: _.get(res, 'body.data.colorMainLigther'),
          };
          this.setAppColors(colors);
          // save imgs uri in cookies
          this.cookieService.set('logo-vertical', _.get(res, 'body.data.imgLoginUri'));
          this.cookieService.set('logo-horizontal', _.get(res, 'body.data.imgHeaderUri'));
          this.cookieService.set('icon', _.get(res, 'body.data.imgIconUri'));
          // set favicon
          this.setFavicon();
        },
        error: (err: any) => {
          this.logger.error('[authService] getAnonymousFeatures err:', err);
        }
      });
  }

  private setAppColors(colors: { c: string; m: string; mD: string; mL: string; }): void {
    const root = document.documentElement;
    if(colors.c) root.style.setProperty('--complementary-color', colors.c);
    if(colors.m) root.style.setProperty('--main-color', colors.m);
    if(colors.mD) root.style.setProperty('--main-color-darker', colors.mD);
    if(colors.mL) root.style.setProperty('--main-color-lighter', colors.mL);
  }

  private setNavigatorLang(): void {
    const navLang = navigator.language ? _.first(_.split(navigator.language, '-')) : null;
    const langToSet = navLang ? navLang : 'es';
    this.translate.use(langToSet || 'es');
    moment.locale(langToSet || 'es');
  }

  private setFavicon(): void {
    this.authService.getLogo('icon').subscribe(base64 => {
      if (base64) {
        // set png type
        const favicon = base64.replace(
          'data:application/octet-stream;base64,',
          'data:image/png;base64,'
        );
        // get link
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        // if no link, create it
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        // set link ref
        link.href = favicon;
      }
    });
  }
}
